import { formatPrice } from '@utils/prices';
import { shopifyAdminIdToVariantGraphqlId } from '@utils/shopifyIds';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DetailsValues } from '../../../components/Checkout/Details';
import {
  FramingChoice,
  FramingProduct,
  FramingSelections,
} from '../../../components/Checkout/Framing/types';
import {
  getInitialSelection,
  parseVariantTitle,
  resolveChoiceForProduct,
} from '../../../components/Checkout/Framing/utils/framePricing';
import { applyChoiceToSelections } from '../../../components/Checkout/Framing/utils/frameSelections';
import { OrderReviewItem } from '../../../components/Checkout/OrderReview';
import { DEFAULT_CURRENCY_CODE } from '../../../constants/locations';
import { useDrawStage } from '../../../hooks/useDrawStage';
import { useAuth } from '../../../providers/AuthProvider';
import { useDraws } from '../../../providers/DrawProvider';
import { useProducts } from '../../../providers/ProductProvider';
import {
  GeneratedMoneyInput,
  GeneratedProductReleaseType,
} from '../../../types/generated';
import { CountryCode, CurrencyCode } from '../../../types/Locations';
import { CHECKOUT_UNFRAMED, MIN_QUANTITY } from '../constants';
import {
  CheckoutEntryController,
  CheckoutEntrySelection,
  CheckoutFlowType,
  CheckoutIntentKind,
} from '../types';
import { buildAmlLines } from '../utils/aml';
import { trackCheckoutEntrySuccess } from '../utils/analytics';
import { acceptsBid, minimumBid } from '../utils/auctionBid';
import { resolveCheckoutFlowType } from '../utils/flowType';
import { buildFramingProducts } from '../utils/framingProducts';
import { resolveFrameByVariantId } from '../utils/framingQuery';
import { resolveCheckoutIntentKind } from '../utils/intentKind';
import { CheckoutOrderTotals } from '../utils/orderTotals';
import { PrivateRoomQuery } from '../utils/privateRoomQuery';
import { resolveReleaseType } from '../utils/releaseType';
import { DEFAULT_CHECKOUT_STEP } from '../utils/steps';
import { CheckoutWork } from '../utils/workView';
import { useCheckoutAmlCheck } from './useCheckoutAmlCheck';
import { useCheckoutAttributes } from './useCheckoutAttributes';
import { useCheckoutDisplayPrice } from './useCheckoutDisplayPrice';
import { useCheckoutHandoff } from './useCheckoutHandoff';
import {
  CheckoutSummaryProps,
  useCheckoutOrderValues,
} from './useCheckoutOrderValues';
import {
  CheckoutRedirectReturnController,
  useCheckoutRedirectReturn,
} from './useCheckoutRedirectReturn';
import { CheckoutStepController, useCheckoutStep } from './useCheckoutStep';
import { useDrawEntry } from './useDrawEntry';
import { useLinkedDrawEntry } from './useLinkedDrawEntry';
import { usePrivateRoomCheckout } from './usePrivateRoomCheckout';
import { useWorks } from './useWorks';

export type UseCheckoutArgs = {
  initialFrameVariantId?: string | null;
  initialWorkIds?: string[];
  privateRoom?: PrivateRoomQuery | null;
  productShopifyId: string;
  productSlug: string;
};

export type CheckoutController = {
  acceptsBid: (amount: number) => boolean;
  allowMultipleSelections: boolean;
  productShopifyId: string;
  artistName: string;
  bid: number | null;
  checkoutLoading: boolean;
  clientSecret: string | null;
  confirmEntry: () => Promise<boolean>;
  currencyCode: CurrencyCode;
  detailsValues: DetailsValues | null;
  drawCloseAt: string | null;
  enteredWorkIds: string[];
  flowType: CheckoutFlowType | null;
  intentId: string | null;
  intentKind: CheckoutIntentKind | null;
  isAuction: boolean;
  isLinkedDraw: boolean;
  isSingleDraw: boolean;
  prepareIntent: (details: {
    shippingAddressId: string | null;
    paymentMethodId: string | null;
    preOrder: boolean;
    countryCode: CountryCode | null;
    currencyCode: CurrencyCode;
  }) => Promise<boolean>;
  goToShopifyCheckout: () => Promise<void>;
  framingDialog: {
    close: () => void;
    confirm: (choice: FramingChoice, applyToAll?: boolean) => void;
    openFor: (productId: string) => void;
    showApplyToAll: boolean;
    targetProduct: FramingProduct | null;
  };
  framingProducts: FramingProduct[];
  framingSelections: FramingSelections;
  hasPendingFraming: boolean;
  identityVerificationRequired: boolean;
  isPreOrderFlow: boolean;
  maxQuantity: number;
  minimumBid: number;
  orderedItems: OrderReviewItem[];
  orderTotals: CheckoutOrderTotals;
  placeBid: (amount: number) => void;
  preOrdersEnabled: boolean;
  productSlug: string;
  redirectReturn: CheckoutRedirectReturnController;
  selectedWorkIds: string[];
  setDetailsValues: (values: DetailsValues) => void;
  setFramingSelections: (selections: FramingSelections) => void;
  setMaxQuantity: (maxQuantity: number) => void;
  step: CheckoutStepController;
  submitError: boolean;
  summaryProps: CheckoutSummaryProps;
  toggleWork: (productId: string, quantity?: number) => void;
  trackEntrySuccess: () => void;
  works: CheckoutWork[];
  workTitle: string;
};

const withStartingFrames = (
  chosen: FramingSelections,
  starting: FramingSelections,
) => {
  const pending = Object.keys(starting).some((productId) => {
    return chosen[productId] === undefined;
  });

  return pending ? { ...starting, ...chosen } : chosen;
};

export const useCheckout = ({
  initialFrameVariantId = null,
  initialWorkIds = [],
  privateRoom = null,
  productShopifyId,
  productSlug,
}: UseCheckoutArgs): CheckoutController => {
  const handoff = useCheckoutHandoff();
  const buildCustomAttributes = useCheckoutAttributes();
  const privateRoomCheckout = usePrivateRoomCheckout({
    privateRoom,
    productShopifyId,
  });
  const roomVariantIds = useMemo(() => {
    if (!privateRoom) return null;

    return Object.keys(privateRoomCheckout.room?.unlockCodes ?? {});
  }, [privateRoom, privateRoomCheckout.room]);
  const [shippingCountryCode, setShippingCountryCode] =
    useState<CountryCode | null>(null);
  const { isLoggedIn, user } = useAuth();
  const { getProduct } = useProducts();
  const {
    currentWinningBidByDrawId,
    linkedDraws,
    mainDraw,
    selectedDraws,
    setWinningDrawsCount: setMaxQuantity,
    winningDrawsCount: maxQuantity,
  } = useDraws();
  const [bid, setBid] = useState<number | null>(null);
  const [rejectedFloor, setRejectedFloor] =
    useState<GeneratedMoneyInput | null>(null);
  const [chosenFraming, setChosenFraming] = useState<FramingSelections>({});
  const [detailsValues, setDetailsValues] = useState<DetailsValues | null>(
    null,
  );
  const [framingDialogProductId, setFramingDialogProductId] = useState<
    string | null
  >(null);

  const productEntry = getProduct(productShopifyId);
  const releaseType = privateRoom
    ? GeneratedProductReleaseType.Private
    : productEntry?.backendProduct?.releaseType;
  const stage = useDrawStage(mainDraw?.draw);
  const releaseFlowType = resolveCheckoutFlowType({ releaseType, stage });
  const isAuction = releaseFlowType === 'auction';
  const step = useCheckoutStep(isAuction);
  const flowType =
    releaseFlowType === 'draw' && detailsValues?.preOrderOptIn
      ? 'preOrder'
      : releaseFlowType;
  const isPreOrderFlow = flowType === 'preOrder';
  const allowMultipleSelections = flowType === 'timed' || flowType === 'fcfs';
  const intentKind = resolveCheckoutIntentKind(mainDraw?.draw);
  const isLinkedDraw = intentKind === 'linked';
  const isSingleDraw = intentKind === 'single';
  const { isDraw } = resolveReleaseType(releaseType);

  const bidRules = useMemo(() => {
    return {
      floor:
        rejectedFloor ??
        (mainDraw && currentWinningBidByDrawId[mainDraw.draw.id]) ??
        mainDraw?.draw.auctionWinningFloorPrice,
      promptUserAboutWinningBid:
        rejectedFloor !== null || mainDraw?.draw.promptUserAboutWinningBid,
      reserve: mainDraw?.draw.auctionReservePrice,
    };
  }, [currentWinningBidByDrawId, mainDraw, rejectedFloor]);
  const bidCurrencyCode = (mainDraw?.draw.auctionReservePrice?.currencyCode ??
    DEFAULT_CURRENCY_CODE) as CurrencyCode;
  const entryBid = useMemo<GeneratedMoneyInput | null>(() => {
    if (!isAuction || bid === null) return null;

    return { amount: String(bid), currencyCode: bidCurrencyCode };
  }, [bid, bidCurrencyCode, isAuction]);

  const worksController = useWorks({
    initialWorkIds,
    isDraw,
    productShopifyId,
    roomVariantIds,
    step,
  });
  const { enteredWorkIds, selectedWorkIds } = worksController;

  const allDraws = useMemo(
    () => (mainDraw ? [mainDraw, ...linkedDraws] : linkedDraws),
    [linkedDraws, mainDraw],
  );

  const productIdByDrawId = useMemo(
    () => new Map(allDraws.map((draw) => [draw.draw.id, draw.draw.productId])),
    [allDraws],
  );

  const amlCheck = useCheckoutAmlCheck({ countryCode: shippingCountryCode });

  const variantIdForProduct = useCallback(
    (productId: string | undefined): string | null | undefined => {
      return productId
        ? getProduct(productId)?.shopifyPurchaseDetails?.shopifyVariantId
        : undefined;
    },
    [getProduct],
  );

  const linkedEntry = useLinkedDrawEntry({
    drawCloseAt: mainDraw?.draw.closeAt ?? null,
    multisetID: mainDraw?.draw.multisetID || null,
    onConfirmed: (intent) => {
      return amlCheck.check(
        buildAmlLines(
          intent.lineItems.map((lineItem) => ({
            frameItems: lineItem.frameItems,
            variantId: variantIdForProduct(
              productIdByDrawId.get(lineItem.drawId),
            ),
          })),
        ),
        intent.maxQuantity,
      );
    },
    productIdByDrawId,
  });

  const drawVariantId = mainDraw?.draw.productVariantId
    ? shopifyAdminIdToVariantGraphqlId(mainDraw.draw.productVariantId)
    : undefined;

  const handleBidTooLow = useCallback(
    (floor: GeneratedMoneyInput) => {
      setRejectedFloor(floor);
      step.goToStep('bid');
    },
    [step],
  );

  const singleEntry = useDrawEntry({
    drawId: mainDraw?.draw.id ?? null,
    onBidTooLow: handleBidTooLow,
    onConfirmed: (intent) => {
      return amlCheck.check(
        buildAmlLines([
          {
            auctionBid: intent.bid,
            frameItems: intent.frameItems,
            variantId: intent.bid
              ? drawVariantId
              : variantIdForProduct(mainDraw?.draw.productId),
          },
        ]),
        1,
      );
    },
    productId: mainDraw?.draw.productId ?? null,
  });

  const entriesByKind: Record<CheckoutIntentKind, CheckoutEntryController> = {
    linked: linkedEntry,
    single: singleEntry,
  };
  const entry = intentKind ? entriesByKind[intentKind] : null;

  const {
    currencyCode,
    ready: displayPriceReady,
    toDisplayPrice,
  } = useCheckoutDisplayPrice({
    countryCode: shippingCountryCode,
    currencyCode:
      entry?.quoteCurrencyCode ?? (isAuction ? bidCurrencyCode : null),
  });

  const confirmEntryById = useCallback(
    async (intentId: string): Promise<boolean> => {
      return entry ? entry.confirmEntry(intentId) : false;
    },
    [entry],
  );

  const redirectReturn = useCheckoutRedirectReturn(confirmEntryById);

  const lowestBid = minimumBid(bidRules);

  const works = useMemo(
    () =>
      worksController.works.map((work) => ({
        ...work,
        price: isAuction ? (bid ?? lowestBid) : toDisplayPrice(work.price),
      })),
    [bid, isAuction, lowestBid, toDisplayPrice, worksController.works],
  );

  const worksById = useMemo(
    () => new Map(works.map((work) => [work.id, work])),
    [works],
  );

  const selectedWorks = useMemo(
    () =>
      selectedWorkIds
        .map((productId) => worksById.get(productId))
        .filter((work): work is CheckoutWork => Boolean(work)),
    [selectedWorkIds, worksById],
  );

  const framingProductsBase = useMemo(() => {
    return buildFramingProducts({
      getEntry: (shopifyId) => getProduct(shopifyId),
      selectedWorks,
    }).map((product) => ({
      ...product,
      frameOptions: product.frameOptions.map((option) => ({
        ...option,
        variants: option.variants.map((variant) => ({
          ...variant,
          consolidatedVariants: variant.consolidatedVariants.map(
            (consolidatedVariant) => ({
              ...consolidatedVariant,
              price: toDisplayPrice(consolidatedVariant.price),
            }),
          ),
        })),
      })),
    }));
  }, [getProduct, selectedWorks, toDisplayPrice]);

  const seededFrame = useMemo(() => {
    if (!initialFrameVariantId) return undefined;

    const product = framingProductsBase.find(
      (framingProduct) => framingProduct.id === productShopifyId,
    );
    if (!product) return undefined;
    if (initialFrameVariantId === CHECKOUT_UNFRAMED) return null;

    return resolveFrameByVariantId(product, initialFrameVariantId) ?? undefined;
  }, [framingProductsBase, initialFrameVariantId, productShopifyId]);

  const includedFrames = useMemo(() => {
    const frames: FramingSelections = {};

    framingProductsBase.forEach((product) => {
      if (!product.framedByDefault) return;

      const frame = getInitialSelection(product);
      if (frame) frames[product.id] = frame;
    });

    return frames;
  }, [framingProductsBase]);

  const startingFrames = useMemo(() => {
    if (seededFrame === undefined) return includedFrames;

    return { ...includedFrames, [productShopifyId]: seededFrame };
  }, [includedFrames, productShopifyId, seededFrame]);

  const framingSelections = useMemo(() => {
    return withStartingFrames(chosenFraming, startingFrames);
  }, [chosenFraming, startingFrames]);

  const entrySelection = useMemo<CheckoutEntrySelection>(
    () => ({ bid: entryBid, framingSelections, maxQuantity, selectedDraws }),
    [entryBid, framingSelections, maxQuantity, selectedDraws],
  );

  const quote = useMemo(() => {
    return entry?.covers(entrySelection) ? entry.quote : null;
  }, [entry, entrySelection]);

  const bidText = useMemo(() => {
    if (!isAuction) return undefined;

    return bid === null ? '–' : String(formatPrice(bid, currencyCode));
  }, [bid, currencyCode, isAuction]);

  const framableWorkIds = useMemo(
    () => new Set(framingProductsBase.map((product) => product.id)),
    [framingProductsBase],
  );

  const { orderTotals, orderedItems, summaryProps } = useCheckoutOrderValues({
    allowMultipleSelections,
    bid: bidText,
    currencyCode,
    framableWorkIds,
    framingSelections,
    maxQuantity,
    quote,
    selectedWorks,
    showPreference: isLinkedDraw,
    step: step.step,
  });

  const framingProducts = useMemo(
    () =>
      framingProductsBase.map((product) => ({
        ...product,
        initialFrame: framingSelections[product.id],
      })),
    [framingProductsBase, framingSelections],
  );

  const hasPendingFraming = useMemo(
    () =>
      framingProducts.some(
        (product) => framingSelections[product.id] === undefined,
      ),
    [framingProducts, framingSelections],
  );

  const framingDialogProduct = useMemo(
    () =>
      framingProducts.find(
        (product) => product.id === framingDialogProductId,
      ) ?? null,
    [framingDialogProductId, framingProducts],
  );

  const openFramingDialog = useCallback(
    (productId: string) => {
      setFramingDialogProductId(productId);
      step.goToStep(DEFAULT_CHECKOUT_STEP);
    },
    [step],
  );

  const closeFramingDialog = useCallback(
    () => setFramingDialogProductId(null),
    [],
  );

  const resetEntryUnlessCovered = useCallback(
    (next: Partial<CheckoutEntrySelection>) => {
      if (!entry || entry.covers({ ...entrySelection, ...next })) return;

      entry.reset();
      setDetailsValues(null);
    },
    [entry, entrySelection],
  );

  const applyFramingSelections = useCallback(
    (selections: FramingSelections) => {
      setChosenFraming(selections);
      resetEntryUnlessCovered({ framingSelections: selections });
    },
    [resetEntryUnlessCovered],
  );

  const placeBid = useCallback(
    (amount: number) => {
      setBid(amount);
      resetEntryUnlessCovered({
        bid: { amount: String(amount), currencyCode: bidCurrencyCode },
      });
    },
    [bidCurrencyCode, resetEntryUnlessCovered],
  );

  const isBidAccepted = useCallback(
    (amount: number) => {
      return acceptsBid(amount, bidRules);
    },
    [bidRules],
  );

  const changeMaxQuantity = useCallback(
    (nextMaxQuantity: number) => {
      setMaxQuantity(nextMaxQuantity);
      resetEntryUnlessCovered({ maxQuantity: nextMaxQuantity });
    },
    [resetEntryUnlessCovered, setMaxQuantity],
  );

  const confirmFramingDialog = useCallback(
    (choice: FramingChoice, applyToAll = false) => {
      if (framingDialogProduct) {
        applyFramingSelections(
          applyChoiceToSelections({
            applyToAll,
            choice,
            previous: framingSelections,
            products: framingProducts,
            targetIds: [framingDialogProduct.id],
          }),
        );
      }
      setFramingDialogProductId(null);
    },
    [
      applyFramingSelections,
      framingDialogProduct,
      framingProducts,
      framingSelections,
    ],
  );

  const isStepReachable = useMemo(() => {
    if (step.step === 'works') return true;

    if (redirectReturn.isReturn) {
      return !(step.step === 'success' && redirectReturn.status === 'failed');
    }

    const worksComplete = selectedWorkIds.length > 0 && !hasPendingFraming;
    if (step.step === 'bid') return worksComplete;

    const bidComplete = worksComplete && (!isAuction || entryBid !== null);
    if (step.step === 'details') return bidComplete;

    return bidComplete && detailsValues !== null && Boolean(isLoggedIn);
  }, [
    detailsValues,
    entryBid,
    hasPendingFraming,
    isAuction,
    isLoggedIn,
    redirectReturn.isReturn,
    redirectReturn.status,
    selectedWorkIds,
    step.step,
  ]);

  useEffect(() => {
    if (isStepReachable) return;

    step.goToStep(
      redirectReturn.status === 'failed' ? 'review' : DEFAULT_CHECKOUT_STEP,
    );
  }, [isStepReachable, redirectReturn.status, step]);

  useEffect(() => {
    if (!displayPriceReady) return;

    setChosenFraming((previous) => {
      return withStartingFrames(previous, startingFrames);
    });
  }, [displayPriceReady, startingFrames]);

  useEffect(() => {
    if (!allowMultipleSelections) return;

    setChosenFraming((previous) => {
      let changed = false;
      const next = { ...previous };

      framingProducts.forEach((product) => {
        const selection = previous[product.id];
        if (!selection) return;

        const consolidatedQuantity =
          selection.variant.consolidatedVariants[0]?.quantity ?? MIN_QUANTITY;
        const quantity = worksById.get(product.id)?.quantity ?? MIN_QUANTITY;
        if (consolidatedQuantity === quantity) return;

        const { glazing, mounting } = parseVariantTitle(
          selection.variant.title,
        );
        const resolved = resolveChoiceForProduct(product, {
          glazing,
          mounting,
          styleTitle: selection.title,
        });
        if (resolved) {
          next[product.id] = resolved;
          changed = true;
        }
      });

      return changed ? next : previous;
    });
  }, [allowMultipleSelections, framingProducts, worksById]);

  const toggleWork = useCallback(
    (productId: string, quantity?: number) => {
      const change = worksController.toggleWork(productId, quantity);
      if (change) resetEntryUnlessCovered(change);
    },
    [resetEntryUnlessCovered, worksController],
  );

  const backendProduct = productEntry?.backendProduct ?? mainDraw?.product;

  const selectedDrawEntries = useMemo(
    () => allDraws.filter((item) => selectedDraws.includes(item.draw.id)),
    [allDraws, selectedDraws],
  );

  const trackEntrySuccess = useCallback(() => {
    trackCheckoutEntrySuccess({
      artistId: backendProduct?.artistIds?.[0],
      drawType: mainDraw?.draw.drawType,
      framingSelections,
      intentId: entry?.intentId ?? null,
      isPreOrder: isPreOrderFlow,
      preOrderOptIn:
        Boolean(mainDraw?.draw.preorderEnabled) &&
        Boolean(detailsValues?.preOrderOptIn),
      selectedEntries: selectedDrawEntries,
      user,
    });
  }, [
    backendProduct,
    detailsValues,
    entry,
    framingSelections,
    isPreOrderFlow,
    mainDraw,
    selectedDrawEntries,
    user,
  ]);

  const prepareIntent = useCallback(
    async (details: {
      shippingAddressId: string | null;
      paymentMethodId: string | null;
      preOrder: boolean;
      countryCode: CountryCode | null;
      currencyCode: CurrencyCode;
    }): Promise<boolean> => {
      redirectReturn.clear();
      setShippingCountryCode(details.countryCode);

      if (!entry) return true;

      return entry.submit(entrySelection, {
        currencyCode: entryBid ? bidCurrencyCode : details.currencyCode,
        customAttributes: await buildCustomAttributes({
          canBeFramed: framingProductsBase.length > 0,
        }),
        paymentMethodId: details.paymentMethodId,
        preOrder: details.preOrder,
        shippingAddressId: details.shippingAddressId,
      });
    },
    [
      bidCurrencyCode,
      buildCustomAttributes,
      entry,
      entryBid,
      entrySelection,
      framingProductsBase.length,
      redirectReturn,
    ],
  );

  const confirmEntry = useCallback(async (): Promise<boolean> => {
    if (!entry?.intentId) return false;

    return entry.confirmEntry(entry.intentId);
  }, [entry]);

  const identityVerificationRequired = amlCheck.required;

  const goToShopifyCheckout = useCallback(async () => {
    if (privateRoomCheckout.loading || privateRoomCheckout.leaveIfExpired()) {
      return;
    }

    const items = selectedWorks.flatMap((work) => {
      const product = getProduct(work.id)?.shopifyPurchaseDetails;
      if (!product?.shopifyVariantId) return [];

      return [
        {
          frame: framingSelections[work.id] ?? null,
          product,
          quantity: work.quantity ?? 1,
        },
      ];
    });
    if (!items.length) return;

    await handoff.goToCheckout({
      currency: currencyCode,
      items,
      ...(privateRoomCheckout.room
        ? { privateRoom: privateRoomCheckout.room }
        : {}),
    });
  }, [
    currencyCode,
    framingSelections,
    getProduct,
    handoff,
    privateRoomCheckout,
    selectedWorks,
  ]);

  return useMemo(
    () => ({
      acceptsBid: isBidAccepted,
      allowMultipleSelections,
      productShopifyId,
      artistName: backendProduct?.artists?.[0]?.name ?? '',
      bid,
      checkoutLoading:
        handoff.loading ||
        privateRoomCheckout.loading ||
        Boolean(entry?.loading) ||
        Boolean(entry?.confirming),
      clientSecret: entry?.clientSecret ?? null,
      confirmEntry,
      currencyCode,
      detailsValues,
      drawCloseAt: mainDraw?.draw.closeAt ?? null,
      enteredWorkIds,
      flowType,
      goToShopifyCheckout,
      framingDialog: {
        close: closeFramingDialog,
        confirm: confirmFramingDialog,
        openFor: openFramingDialog,
        showApplyToAll: framingProducts.length > 1,
        targetProduct: framingDialogProduct,
      },
      framingProducts,
      framingSelections,
      hasPendingFraming,
      identityVerificationRequired,
      intentId: entry?.intentId ?? null,
      intentKind,
      isAuction,
      isLinkedDraw,
      isPreOrderFlow,
      isSingleDraw,
      maxQuantity,
      minimumBid: lowestBid,
      orderedItems,
      orderTotals,
      placeBid,
      preOrdersEnabled:
        releaseFlowType !== 'preOrder' &&
        (mainDraw?.draw.preorderEnabled ?? false),
      prepareIntent,
      productSlug,
      redirectReturn,
      selectedWorkIds,
      setDetailsValues,
      setFramingSelections: applyFramingSelections,
      setMaxQuantity: changeMaxQuantity,
      step,
      submitError: entry?.error ?? false,
      summaryProps,
      toggleWork,
      trackEntrySuccess,
      works,
      workTitle: backendProduct?.title ?? '',
    }),
    [
      allowMultipleSelections,
      applyFramingSelections,
      productShopifyId,
      backendProduct,
      bid,
      changeMaxQuantity,
      closeFramingDialog,
      confirmEntry,
      confirmFramingDialog,
      currencyCode,
      detailsValues,
      enteredWorkIds,
      entry,
      flowType,
      framingDialogProduct,
      framingProducts,
      framingSelections,
      goToShopifyCheckout,
      handoff.loading,
      privateRoomCheckout.loading,
      hasPendingFraming,
      identityVerificationRequired,
      intentKind,
      isAuction,
      isBidAccepted,
      isLinkedDraw,
      isPreOrderFlow,
      isSingleDraw,
      lowestBid,
      mainDraw,
      maxQuantity,
      openFramingDialog,
      orderTotals,
      orderedItems,
      placeBid,
      prepareIntent,
      productSlug,
      redirectReturn,
      releaseFlowType,
      selectedWorkIds,
      step,
      summaryProps,
      toggleWork,
      trackEntrySuccess,
      works,
    ],
  );
};
